import { appendManufacturingLinesSource } from 'components/frank/utils/manufacturingLinesSource.js';
import {
  readSectionBobbin,
  readSectionMachineTypes,
  readSectionDisabledFilters,
  readSectionAreaRatioPercent,
  readSectionMaterialUserPriceId,
  readSectionCartonGsm,
} from 'components/frank/utils/manufacturingLineListUrlState.js';

// The area-ratio cap (a PERCENT: "110" == a printing bed up to 110% larger in area than the sheet is kept) that
// the /mcc Debug input starts on. Seeded from the server so it matches the applied default: the server reads
// the frank_machine_area_ratio_percent SystemParam (admin-tunable from the database, no deploy), which itself
// falls back to the filter's shipped default -- no second hardcoded value to drift. The literal fallback here
// is only for surfaces that do not set the global, e.g. JS tests.
export const DEFAULT_AREA_RATIO_PERCENT = window.frank_machine_area_ratio_percent ?? 110;

// Append the quantity (count) to a fetch's query. The client sends count on all three /mcc fetches so the
// server scores / solves against the count the page is SHOWING -- not whatever user_quote.count happens to be,
// which the count field persists ASYNCHRONOUSLY (a server that read the quote could score a not-yet-saved
// quantity). It also puts count in the URL, so it is part of the response-cache key. Absent count (the legacy
// stacked view, which never passes it) is simply not appended, and the server falls back to the quote's count.
export const setCountParam = (query, count) => {
  if (count !== undefined && count !== null) { query.set('count', count); }
};

// The /mcc /manufacturing-line-lists/<index> URL for a section, built from its filters. ONE source, so the
// Manufacturing Line stage and the guided setup wizard's add-a-machine step (which explains the same listing
// before that stage is reached, see SectionDisabledManufacturingLines) ask with byte-identical params.
export const sectionListingUrl = (navSectionIndex, {
  bobbinEnabled, selectedMaterialUserPriceId, disabledFilters, selectedTypes, selectedCartonGsm, areaRatioPercent, count
}) => {
  const query = new URLSearchParams();
  if (bobbinEnabled !== undefined) { query.set('bobbin_enabled', bobbinEnabled); }
  // The owner's chosen material row. It is ON the listing query because the finder and scorer both use it: the
  // material feeds the section's sheets (which lines can be found) and prices every solution the score is the
  // minimum of (Frank::Coordinator#offered_solution_cards -> MaterialScore), so BRISTOL and KROMO score the same
  // section differently. Absent (no choice) lets the server auto-pick the lowest-id row. Left off the listing
  // query, every material scored as that auto-pick -- the Skor never moved when the Malzeme changed.
  if (selectedMaterialUserPriceId) { query.set('material_user_price_id', selectedMaterialUserPriceId); }
  disabledFilters.forEach((key) => query.append('disabled_filters[]', key));
  Object.entries(selectedTypes).forEach(([key, types]) => {
    // A query string cannot carry an empty array, and "absent" already means "use the default" -- so an
    // empty selection is sent as one blank entry, which the server reads as "nothing ticked".
    if (types.length === 0) {
      query.append(`${key}[]`, '');
      return;
    }
    types.forEach((type) => query.append(`${key}[]`, type));
  });
  // The owner's chosen carton GSM (a carton_lamination section only) -- it sets the printing material's
  // thickness, so it IS on the listing query (it changes which printing machines are found), unlike the
  // material choice above. Absent when none is picked, so the server applies its default (300).
  if (selectedCartonGsm !== null) { query.set('carton_gsm', selectedCartonGsm); }
  // The admin's area-ratio cap, so the listing is reduced against the same value the solve and scoring use.
  query.set('area_ratio_percent', areaRatioPercent);
  // The listing itself is count-independent (it lists which plans CAN make the section, not their cost), but
  // count is sent so a quantity change re-fetches it and so re-mints the `listing` object the best-score effect
  // keys off -- which is what re-triggers the (count-dependent) scoring. It also keeps the cache key uniform:
  // count rides in the URL on all three fetches. The server ignores it here.
  setCountParam(query, count);
  // Which account this listing is found and scored against -- keyed into the request so a Standard listing is
  // never reused for an Own one (and the server answers the source asked for).
  appendManufacturingLinesSource(query);
  const queryString = query.toString();

  return `/mcc/${window.user_quote_id}/manufacturing-line-lists/${navSectionIndex}${queryString ? `?${queryString}` : ''}`;
};

// The listing a section's Manufacturing Line stage asks for when it mounts: its filters as restored from the address
// bar, the same readers ManufacturingLineLists and LayoutAndQuoteBuilderNavSection (the bobbin) seed their state from.
export const restoredSectionListingUrl = (navSectionIndex, count) => sectionListingUrl(navSectionIndex, {
  bobbinEnabled: readSectionBobbin(navSectionIndex),
  selectedMaterialUserPriceId: readSectionMaterialUserPriceId(navSectionIndex),
  disabledFilters: readSectionDisabledFilters(navSectionIndex),
  selectedTypes: readSectionMachineTypes(navSectionIndex),
  selectedCartonGsm: readSectionCartonGsm(navSectionIndex),
  areaRatioPercent: readSectionAreaRatioPercent(navSectionIndex) ?? DEFAULT_AREA_RATIO_PERCENT,
  count,
});
